import { defineStore } from "pinia";
import { AirlineSettings } from "./entity";
import { AirlineSettingsList } from "./list.gen";
import type {
  AirlineSettingsStore,
  AirlineSettingsStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const airlineSettingsList = new AirlineSettingsList();
export const useAirlineSettingsStore = defineStore("airlineSettingsStore", {
  state: (): AirlineSettingsStoreState => ({
    activeID: null,
    cache: {},
    list: [] as AirlineSettings[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-AirlineSettings", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.AirlineSettings"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(airlineSettings: AirlineSettings): Promise<void> {
      // Set active
      await airlineSettings
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = airlineSettings.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(airlineSettings: AirlineSettings): void {
      const id = airlineSettings.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(airlineSettings);

        // Set active
        if (this.active === null) {
          this.activeID = airlineSettings.id;
        }
      } else {
        this.reinsert(airlineSettings);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as AirlineSettings[]).map((x: any) => {
        const airlineSettings = new AirlineSettings({});
        airlineSettings.set(x as JSONData);
        return airlineSettings;
      });
    },
    async delete(airlineSettings: AirlineSettings): Promise<void> {
      await airlineSettings
        .delete()
        .then(() => {
          this.remove(airlineSettings);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const airlineSettings = (this.list as AirlineSettings[]).find(
        (x: AirlineSettings) => x.id === id,
      );
      if (airlineSettings !== undefined) {
        await this.delete(airlineSettings);
      } else {
        throw new Error("Could not find AirlineSettings with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as AirlineSettings[]).some(
        (x: AirlineSettings) => x.id === id,
      );
    },
    get(id: string): AirlineSettings | null {
      return (
        (this.list as AirlineSettings[]).find(
          (x: AirlineSettings) => x.id === id,
        ) || null
      );
    },
    reinsert(airlineSettings: AirlineSettings): void {
      const index = (this.list as AirlineSettings[]).findIndex(
        (x: AirlineSettings) => x.id === airlineSettings.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          airlineSettings,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(airlineSettings);
      }
      this.updateKey += 1;
    },
    remove({ id }: AirlineSettings): void {
      const index = (this.list as AirlineSettings[]).findIndex(
        (x: AirlineSettings) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<AirlineSettings> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const airlineSettings = this.get(id);
        if (airlineSettings !== null) {
          return airlineSettings;
        }
      }

      // Retrieve airlineSettings
      const airlineSettings = new AirlineSettings({});

      // Get airlineSettings
      await airlineSettings.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate airlineSettings
        await this.activate(entity);
      });

      return airlineSettings;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: AirlineSettings[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as AirlineSettings[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await airlineSettingsList.getAll().then(async (list) => {
        list.forEach((airlineSettings) => {
          this.reinsert(airlineSettings);
        });

        // Activate airlineSettings
        if (list.length > 0) {
          if (airlineSettingsList.active !== null) {
            this.activeID = airlineSettingsList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("airline_settings_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as AirlineSettings[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AirlineSettings[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map(
            (id: string) => this.get(id) as AirlineSettings,
          ),
        };
      }

      // Retrieve list
      const items = await airlineSettingsList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((airlineSettings) => {
            this.reinsert(airlineSettings);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AirlineSettings) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AirlineSettings[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map(
            (id: string) => this.get(id) as AirlineSettings,
          ),
        };
      }

      // Retrieve list
      const items = await airlineSettingsList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((airlineSettings) => {
            this.reinsert(airlineSettings);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AirlineSettings) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: AirlineSettings[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map(
            (id: string) => this.get(id) as AirlineSettings,
          ),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await airlineSettingsList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((airlineSettings) => {
            this.reinsert(airlineSettings);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: AirlineSettings) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(airlineSettings: AirlineSettings): Promise<string> {
      // Update airlineSettings
      await airlineSettings.update();

      // Reinsert airlineSettings
      this.reinsert(airlineSettings);

      // Activate airlineSettings
      if (this.active === null) {
        await this.activate(airlineSettings);
      }

      this.emitStoreUpdateEvent();

      return airlineSettings.id;
    },
  },
  getters: {
    active(): AirlineSettings | null {
      if (this.activeID === null) {
        return null;
      }

      const airlineSettings = (this.list as AirlineSettings[]).find(
        (x: AirlineSettings) => x.id === this.activeID,
      );
      if (airlineSettings === undefined) {
        return null;
      }

      return airlineSettings;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createAirlineSettingsStore(): AirlineSettingsStore {
  const store = useAirlineSettingsStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as AirlineSettingsStore; // TODO: Fix this
}
